import { Box, Card, CardContent, Typography } from "@material-ui/core";
import classnames from "classnames";
import { PropsWithChildren, useCallback } from "react";
import { Button, useNotify } from "react-admin";
import {
  FormProvider,
  SubmitHandler,
  useForm,
  useFormContext,
} from "react-hook-form";
import { CompactFormToolbar } from "../../../../../common/form/CompactFormToolbar";
import { CompactSaveButton } from "../../../../../common/form/CompactSaveButton";
import { useAxiosInstance } from "../../../../../../common/hooks/useAxiosInstance";
import { EventApiFactory } from "../../../../../../common/data/service-proxies";
import { handleAxiosError } from "../../../../../../common/data/axiosErrorHandler";
import { HookFormTextInput } from "../../../../../common/form/hook-form/inputs/HookFormTextInput";
import { Check, Clear, Close } from "@material-ui/icons";

interface Props {
  selectedRows: number[];
  currentTrailers?: Array<string | null | undefined>;
  latestEvent?: number;
  onSuccess: () => void;
  onFailed?: () => void;
}

type FormValues = {
  attachedTrailers: string | null;
};

const DefaultCompactFormToolbar = ({ children }: PropsWithChildren<any>) => {
  const {
    formState: { isSubmitting },
  } = useFormContext();
  return (
    <CompactFormToolbar>
      <CompactSaveButton saving={isSubmitting} disabled={isSubmitting} />
      {children}
    </CompactFormToolbar>
  );
};

export const ChangeSelectedEventsTrailerForm = ({
  selectedRows,
  currentTrailers,
  latestEvent,
  onSuccess,
  onFailed,
  ...rest
}: Props) => {
  const notify = useNotify();
  const axios = useAxiosInstance();
  const selectedTrailers = Array.from(
    new Set(currentTrailers?.filter((x) => x?.length)).values()
  ).join(", ");

  const onSubmit = useCallback(
    (data: FormValues) => {
      if (!axios) {
        return;
      }

      return EventApiFactory(undefined, axios.defaults.baseURL, axios)
        .apiAppEventBulksettrailersPost({
          selectedEvents: selectedRows,
          attachedTrailers: data.attachedTrailers,
          isLatestEventSelected:
            !!latestEvent && selectedRows.includes(latestEvent),
        })
        .then(() => {
          onSuccess();
        })
        .catch((e) => {
          if (onFailed) {
            onFailed();
          }

          console.error(e);
          notify(handleAxiosError(e), { type: "error" });
        });
    },
    [axios, selectedRows, latestEvent, notify, onSuccess, onFailed]
  );

  const formContext = useForm<FormValues>({
    mode: "onChange",
  });

  const { handleSubmit, control } = formContext;

  const handleSubmitInternal: SubmitHandler<FormValues> = (data) => {
    return Promise.resolve(onSubmit(data)).catch((e: Error) => {
      notify(e.message ?? "An unexpected error has occurred", "error");
    });
  };

  return (
    <CardContent>
      <FormProvider {...formContext}>
        <form
          className={classnames("simple-form", "form-create-edit-drawer")}
          {...sanitizeRestProps(rest)}
          onSubmit={handleSubmit(handleSubmitInternal, (err) =>
            console.error(err)
          )}>
          <Card>
            <CardContent>
              {/* <div className="ra-input">
                <TextField
                  label="Current trailer(s)"
                  disabled
                  variant="outlined"
                  margin="dense"
                  value={selectedTrailers.length ? selectedTrailers : "(none)"}
                />
              </div> */}
              <div className="ra-input">
                <HookFormTextInput<FormValues>
                  control={control}
                  name="attachedTrailers"
                  label="Attached trailer(s)"
                  variant="outlined"
                  defaultValue={selectedTrailers}
                  options={{ resettable: true }}
                />
              </div>
              <div className="ra-input">
                <Box display="flex" alignItems="center" gridGap={5}>
                  {!!latestEvent && selectedRows.includes(latestEvent) ? (
                    <Check fontSize="small" />
                  ) : (
                    <Clear fontSize="small" />
                  )}
                  <Typography variant="body2">latest event selected</Typography>
                </Box>
              </div>
            </CardContent>
          </Card>
          <DefaultCompactFormToolbar>
            <Button label="Leave unchanged" onClick={onFailed}>
              <Close />
            </Button>
          </DefaultCompactFormToolbar>
        </form>
      </FormProvider>
    </CardContent>
  );
};

const sanitizeRestProps = ({
  active,
  dirty,
  dirtyFields,
  dirtyFieldsSinceLastSubmit,
  dirtySinceLastSubmit,
  error,
  errors,
  form,
  hasSubmitErrors,
  hasValidationErrors,
  initialValues,
  modified = null,
  modifiedSinceLastSubmit,
  save = null,
  submitError,
  submitErrors,
  submitFailed,
  submitSucceeded,
  submitting,
  touched = null,
  valid,
  values,
  visited = null,
  __versions = null,
  ...props
}: any) => props;
